import { useRef } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Mail, Phone, MapPin, Heart } from "lucide-react";
import { CONTACT, SOCIAL, NAV_LINKS, PETITION_URL } from "../lib/constants";
import { usePetition } from "../lib/petitionContext";

// X (Twitter) icon
const XIcon = ({ size = 18, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
        <path d="M18.244 2H21.5l-7.522 8.59L22.75 22h-6.756l-5.292-6.92L4.7 22H1.44l8.06-9.205L1.5 2h6.91l4.79 6.353L18.244 2zm-2.37 18h1.87L7.39 4H5.43l10.444 16z" />
    </svg>
);

// TikTok icon
const TikTokIcon = ({ size = 18, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
        <path d="M16.5 0H12v15.5c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3c.34 0 .67.06.98.16V8.05c-.32-.04-.65-.05-.98-.05C5.13 8 2 11.13 2 15s3.13 7 7 7 7-3.13 7-7V8.5c1.5 1.1 3.34 1.74 5.34 1.74V6.06c-2.83 0-4.84-1.66-4.84-6.06z" />
    </svg>
);

export const Footer = () => {
    const { openSubscribe } = usePetition();
    const subscribeBtnRef = useRef(null);

    return (
    <footer className="bg-navy-900 text-editorial relative overflow-hidden" data-testid="site-footer">
        <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />

        {/* Subscribe strip — simple Subscribe CTA (no email field) that opens
            the in-site SubscribeModal on click. */}
        <div className="border-b border-white/5 relative" data-testid="footer-subscribe-strip">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 md:py-10 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
                <div className="flex items-center gap-3 text-center md:text-left">
                    <span className="hidden md:inline-flex items-center justify-center w-10 h-10 rounded-full bg-coral/15 border border-coral/25 text-coral flex-shrink-0">
                        <Mail size={18} />
                    </span>
                    <div>
                        <p className="overline text-gold mb-1">Stay Connected</p>
                        <p className="text-editorial/85 text-base md:text-lg leading-snug">
                            Get updates on the Hotel Cardiac Emergency Preparedness Act, petition milestones, and ways to help save lives.
                        </p>
                    </div>
                </div>
                <button
                    ref={subscribeBtnRef}
                    type="button"
                    onClick={() => openSubscribe(subscribeBtnRef)}
                    data-testid="footer-subscribe-btn"
                    className="inline-flex items-center justify-center gap-2 text-sm font-semibold px-8 py-3 bg-coral hover:bg-coral-dark text-white transition-colors flex-shrink-0"
                >
                    <Mail className="w-4 h-4" />
                    Subscribe
                </button>
            </div>
        </div>

        {/* Top urgent strip */}
        <div className="border-b border-white/5 relative">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="font-serif italic text-2xl text-editorial/90">
                    Every second matters.
                </p>
                <a
                    href={PETITION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium px-6 py-2.5 bg-coral hover:bg-coral-dark text-white transition-colors"
                    data-testid="footer-petition-btn"
                >
                    Sign the Petition
                </a>
            </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10 relative">
            {/* Brand */}
            <div className="md:col-span-5">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-full bg-editorial text-navy-900 flex items-center justify-center font-serif text-2xl">
                        JC
                    </div>
                    <div>
                        <div className="font-serif text-2xl">JC Law Foundation</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Prepared Hotels · Protected Lives</div>
                    </div>
                </div>
                <p className="text-editorial/70 leading-relaxed max-w-md mb-6">
                    A public-safety advocacy movement dedicated to CPR awareness, AED accessibility, and emergency preparedness in hotels and lodging facilities — so no family faces a cardiac emergency alone.
                </p>
                <div className="flex items-center gap-3">
                    <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-10 h-10 border border-white/15 hover:border-gold hover:text-gold flex items-center justify-center transition-colors">
                        <Instagram size={18} />
                    </a>
                    <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tiktok" className="w-10 h-10 border border-white/15 hover:border-gold hover:text-gold flex items-center justify-center transition-colors">
                        <TikTokIcon />
                    </a>
                    <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-facebook" className="w-10 h-10 border border-white/15 hover:border-gold hover:text-gold flex items-center justify-center transition-colors">
                        <Facebook size={18} />
                    </a>
                    <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="w-10 h-10 border border-white/15 hover:border-gold hover:text-gold flex items-center justify-center transition-colors">
                        <Linkedin size={18} />
                    </a>
                    <a href={SOCIAL.twitter} target="_blank" rel="noopener noreferrer" aria-label="X/Twitter" data-testid="social-twitter" className="w-10 h-10 border border-white/15 hover:border-gold hover:text-gold flex items-center justify-center transition-colors">
                        <XIcon />
                    </a>
                </div>
            </div>

            {/* Explore */}
            <div className="md:col-span-3">
                <h4 className="font-sans text-xs uppercase tracking-[0.22em] text-gold mb-5">Explore</h4>
                <ul className="space-y-3">
                    {NAV_LINKS.map((l) => (
                        <li key={l.path}>
                            <Link to={l.path} className="text-editorial/75 hover:text-editorial transition-colors text-sm">
                                {l.label}
                            </Link>
                        </li>
                    ))}
                    <li><Link to="/donate" className="text-editorial/75 hover:text-editorial transition-colors text-sm">Donate</Link></li>
                </ul>
            </div>

            {/* Contact */}
            <div className="md:col-span-4">
                <h4 className="font-sans text-xs uppercase tracking-[0.22em] text-gold mb-5">Get in Touch</h4>
                <ul className="space-y-4 text-sm">
                    <li className="flex gap-3 items-start">
                        <Mail size={16} className="mt-0.5 text-gold flex-shrink-0" />
                        <div>
                            <a href={`mailto:${CONTACT.general}`} className="text-editorial/85 hover:text-editorial block">{CONTACT.general}</a>
                            <a href={`mailto:${CONTACT.press}`} className="text-editorial/60 hover:text-editorial block text-xs">{CONTACT.press}</a>
                            <a href={`mailto:${CONTACT.partnership}`} className="text-editorial/60 hover:text-editorial block text-xs">{CONTACT.partnership}</a>
                        </div>
                    </li>
                    <li className="flex gap-3 items-center">
                        <Phone size={16} className="text-gold flex-shrink-0" />
                        <a href={`tel:${CONTACT.phoneRaw}`} className="text-editorial/85 hover:text-editorial">{CONTACT.phone}</a>
                    </li>
                    <li className="flex gap-3 items-center">
                        <MapPin size={16} className="text-gold flex-shrink-0" />
                        <span className="text-editorial/85">{CONTACT.location}</span>
                    </li>
                </ul>
            </div>
        </div>

        <div className="border-t border-white/5 relative">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-center md:justify-between gap-3 text-xs text-editorial/60 text-center md:text-left">
                <p className="leading-relaxed">
                    © {new Date().getFullYear()} JC Law Foundation. All Rights Reserved.
                </p>
                <nav aria-label="Footer legal" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 leading-relaxed">
                    <span className="text-editorial/70">Registered 501(c)(3) Nonprofit Organization</span>
                    <span aria-hidden className="text-editorial/25">|</span>
                    <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-editorial hover:underline transition-colors">
                        Privacy Policy
                    </Link>
                    <span aria-hidden className="text-editorial/25">|</span>
                    <Link to="/terms-of-use" data-testid="footer-terms-link" className="hover:text-editorial hover:underline transition-colors">
                        Terms of Use
                    </Link>
                </nav>
            </div>
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-6 -mt-2 flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] text-editorial/40">
                <p>In loving memory of Joshua "JC".</p>
                <p className="flex items-center gap-2">
                    Built with <Heart size={12} className="text-coral animate-heartbeat" /> for safer communities.
                </p>
            </div>
        </div>
    </footer>
    );
};

export default Footer;
